import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { getPublicSettings } from "@/lib/api";

const SettingsContext = createContext(null);
const CONSENT_KEY = "mlv_consent";

const FALLBACK_SETTINGS = {
  contact: { phone: "", email: "", whatsapp_number: "", telegram_username: "" },
  analytics: { ga4_id: "", gtm_id: "", meta_pixel_id: "", tiktok_pixel_id: "", clarity_id: "" },
  consent: { enabled: true, mode: "opt_in" },
  i18n: { default_locale: "en", enabled_locales: ["en", "es", "de", "fr", "uk", "ru"] },
};

function readStoredConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed.analytics === "boolean" && typeof parsed.marketing === "boolean") return parsed;
    return null;
  } catch (e) {
    return null;
  }
}

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(FALLBACK_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [consent, setConsentState] = useState(readStoredConsent());

  const reloadSettings = useCallback(async () => {
    try {
      const data = await getPublicSettings();
      setSettings({ ...FALLBACK_SETTINGS, ...data });
    } catch (e) {
      setSettings(FALLBACK_SETTINGS);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reloadSettings();
  }, [reloadSettings]);

  const setConsent = useCallback((value) => {
    const next = { analytics: !!value.analytics, marketing: !!value.marketing, decided_at: new Date().toISOString() };
    try {
      localStorage.setItem(CONSENT_KEY, JSON.stringify(next));
    } catch (e) {
      /* ignore storage errors */
    }
    setConsentState(next);
  }, []);

  const consentDecided = !!consent;

  // If consent banner is disabled by admin, treat everything as granted.
  const effectiveConsent = useMemo(() => {
    if (settings?.consent?.enabled === false) return { analytics: true, marketing: true };
    if (consent) return { analytics: consent.analytics, marketing: consent.marketing };
    return { analytics: false, marketing: false };
  }, [settings, consent]);

  const value = useMemo(
    () => ({ settings, loading, consent, consentDecided, effectiveConsent, setConsent, reloadSettings }),
    [settings, loading, consent, consentDecided, effectiveConsent, setConsent, reloadSettings]
  );

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
};

export const useSettings = () => {
  const ctx = useContext(SettingsContext);
  return ctx || { settings: FALLBACK_SETTINGS, loading: false, consent: null, consentDecided: false, effectiveConsent: { analytics: false, marketing: false }, setConsent: () => {}, reloadSettings: () => {} };
};
